import { Building2, Percent, Zap, Home, KeyRound, Users } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal, CallButton } from "@/components/Blocks";
import { BUSINESS } from "@/data/site";
import PropertyManagerForm from "@/components/PropertyManagerForm";

const OFFER = [
    { icon: Building2, title: "$115 Service Calls", text: "A preferred service-call rate for property managers, landlords, and rental-property owners — not the standard $155 residential rate." },
    { icon: Percent, title: "Discounted Services & Repairs", text: "Preferred pricing on repair work across your properties, so appliance problems stop eating your maintenance budget." },
    { icon: Zap, title: "Priority Service", text: "Your units move to the front of the line. When a tenant's refrigerator or washer goes down, fast resolution protects your reviews and renewals." },
];

const WHO = [
    { icon: Users, text: "Property managers and property management companies" },
    { icon: KeyRound, text: "Landlords and independent rental-property owners" },
    { icon: Home, text: "Professionals responsible for multiple rental units or properties" },
];

export default function PropertyManagersPage() {
    const crumbs = [
        { name: "Home", path: "/" },
        { name: "Property Managers", path: "/property-managers" },
    ];
    return (
        <div>
            <Seo
                title="Property Manager Appliance Repair Brevard & Seminole FL | Appliance Mania"
                description="Priority appliance repair for property managers and landlords in Brevard & Seminole County, FL. $115 service calls, discounted repairs, priority service. Call (321) 475-2230."
                path="/property-managers"
                schemas={[breadcrumbSchema(crumbs)]}
            />

            <section className="bg-slate-950 text-white dark-grain">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">For property professionals</p>
                        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">
                            Priority Appliance Repair for <span className="text-amber-400">Property Managers &amp; Landlords</span>
                        </h1>
                        <p className="mt-5 text-slate-300 leading-relaxed max-w-xl">
                            A tenant's broken refrigerator is your problem by breakfast. Appliance Mania gives property
                            professionals across Brevard &amp; Seminole Counties a dedicated repair partner: preferred rates,
                            priority scheduling, and a 6-month warranty on every completed repair.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <a href="#pm-request-form"
                                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-6 py-3.5 rounded-xl shadow-lg transition-all hover:-translate-y-0.5"
                                data-testid="pm-hero-form-btn">
                                <Building2 className="h-5 w-5" /> Property Managers — Get Priority Service
                            </a>
                            <CallButton location="pm-hero" testid="pm-hero-call-btn" />
                        </div>
                    </div>
                    <Reveal delay={0.15} className="hidden lg:block">
                        <div className="rounded-3xl overflow-hidden shadow-2xl border border-slate-800 rotate-1">
                            <img src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=70"
                                alt="Residential rental community served by Appliance Mania's property manager program" className="w-full h-[340px] object-cover" loading="eager" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9 sm:py-14">
                <div className="grid gap-6 md:grid-cols-3">
                    {OFFER.map((o, i) => (
                        <Reveal key={o.title} delay={i * 0.08}>
                            <div className="rounded-2xl border border-slate-200 bg-white p-7 h-full shadow-sm" data-testid={`pm-offer-${i}`}>
                                <o.icon className="h-7 w-7 text-red-600" />
                                <h2 className="mt-4 font-heading text-xl font-bold text-slate-900">{o.title}</h2>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{o.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <div className="mt-14 grid lg:grid-cols-2 gap-10 items-start">
                    <Reveal>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Built for how you actually work</h2>
                        <ul className="mt-6 space-y-4">
                            {WHO.map((w) => (
                                <li key={w.text} className="flex items-start gap-3 text-slate-700">
                                    <w.icon className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                                    <span>{w.text}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-6 text-slate-600 leading-relaxed">
                            Submit a request with the property address, tenant details, and access instructions — we coordinate
                            directly, confirm scheduling with you, and complete the repair. One call or one form per issue,
                            across every unit you manage in Brevard and Seminole Counties.
                        </p>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Every completed repair carries the same 6-month warranty we give homeowners — important when you're
                            responsible for the appliance long after we leave.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="rounded-2xl bg-slate-100 border border-slate-200 p-7">
                            <h2 className="font-heading text-xl font-bold text-slate-900">How a property request works</h2>
                            <ol className="mt-5 space-y-4">
                                {[
                                    "Submit the priority request form (or call) with property, tenant, and access details.",
                                    "We contact you to confirm the appointment — nothing is booked until we've confirmed it with you.",
                                    "We coordinate access with you or your tenant, complete the repair, and back it with our 6-month warranty.",
                                ].map((step, i) => (
                                    <li key={step} className="flex gap-4">
                                        <span className="font-heading text-2xl font-extrabold text-amber-500 shrink-0">0{i + 1}</span>
                                        <span className="text-sm text-slate-600 leading-relaxed pt-1">{step}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-slate-950 dark-grain" id="pm-form">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
                    <Reveal>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white text-center">
                            Submit a priority service request
                        </h2>
                        <p className="mt-3 text-slate-400 text-center max-w-xl mx-auto">
                            Tell us about the property and the appliance. We'll confirm scheduling and access with you directly.
                            Prefer to talk it through? Call {BUSINESS.phone}.
                        </p>
                    </Reveal>
                    <div className="mt-8">
                        <PropertyManagerForm />
                    </div>
                </div>
            </section>
        </div>
    );
}
